import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, ArrowLeft, Upload, X, Check } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { NativeSelect } from '../components/NativeSelect';
import CityAutocomplete from '../components/CityAutocomplete';

const PROFILE_TYPES = [
  { value: 'homme', label: 'Homme' },
  { value: 'femme', label: 'Femme' },
  { value: 'couple', label: 'Couple' }
];

const BODY_TYPES = [
  { value: 'mince', label: 'Mince' },
  { value: 'sportif', label: 'Sportif' },
  { value: 'moyenne', label: 'Dans la moyenne' },
  { value: 'rondeurs', label: 'Quelques rondeurs' },
  { value: 'rond', label: 'Rond' }
];

const ETHNICITIES = [
  { value: 'africain', label: 'Africain(e)' },
  { value: 'arabe', label: 'Arabe' },
  { value: 'asiatique', label: 'Asiatique' },
  { value: 'caribeen', label: 'Caribéen(ne)' },
  { value: 'caucasien', label: 'Caucasien(ne)' },
  { value: 'latine', label: 'Latine' },
  { value: 'autre', label: 'Autre' }
];

const EYE_COLORS = [
  { value: 'noirs', label: 'Noirs' },
  { value: 'marrons', label: 'Marrons' },
  { value: 'verts', label: 'Verts' },
  { value: 'bleus', label: 'Bleus' }
];

// Base orientations for everyone
const BASE_ORIENTATIONS = [
  { value: 'hetero', label: 'Hétéro' },
  { value: 'hetero_curieux', label: 'Hétéro curieux' },
  { value: 'bi', label: 'Bi' }
];

// Get orientations based on gender
const getOrientationsForGender = (gender) => {
  if (gender === 'femme') {
    return [...BASE_ORIENTATIONS, { value: 'lesbienne', label: 'Lesbienne' }];
  } else if (gender === 'homme') {
    return [...BASE_ORIENTATIONS, { value: 'gay', label: 'Gay' }];
  }
  return BASE_ORIENTATIONS;
};

const SMOKER_STATUS = [
  { value: 'non_fumeur', label: 'Non fumeur' },
  { value: 'occasionnel', label: 'De temps en temps' },
  { value: 'fumeur', label: 'Fumeur' },
  { value: 'vapoteur', label: 'Vapoteur' }
];

const EXPERIENCE_LEVELS = [
  { value: 'debutant', label: 'Débutant' },
  { value: 'occasionnel', label: 'Occasionnel' },
  { value: 'experimente', label: 'Expérimenté' }
];

const SEXUAL_PREFERENCES = [
  '2+2', 'BDSM', 'Cam', 'Candaulisme', 'Chat', 'Curieux', 'Côte-à-côtisme', 
  'Duo', 'Echangisme', 'Exhibition', 'Extreme', 'Feeling', 'Fétichisme', 
  'Gang bang', 'Hard', 'Mélangisme', 'Papouilles', 'Photos', 'Pluralité', 
  'Scénario', 'Soft', 'Trio', 'Vidéos', 'Voyeurisme'
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  
  // Step 1: Account
  const [accountData, setAccountData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    pseudo: ''
  });

  // Step 2: Profile
  const [profileData, setProfileData] = useState({
    description: '',
    profile_type: '',
    location: '',
    city: '',
    department: '',
    region: '',
    coordinates: null, // [longitude, latitude]
    height: '',
    age: '',
    body_type: '',
    ethnicity: '',
    eye_color: '',
    sexual_orientation: '',
    smoker: '',
    experience_level: '',
    sexual_preferences: [],
    // Couple specific fields
    couple_man: {
      height: '',
      body_type: '',
      ethnicity: '',
      eye_color: '',
      sexual_orientation: '',
      smoker: '',
      experience_level: '',
      age: ''
    },
    couple_woman: {
      height: '',
      body_type: '',
      ethnicity: '',
      eye_color: '',
      sexual_orientation: '',
      smoker: '',
      experience_level: '',
      age: ''
    }
  });

  // Step 3: Photos
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    
    if (accountData.password !== accountData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (accountData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    // Check if pseudo and email are available
    setCheckingAvailability(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/check-availability?pseudo=${encodeURIComponent(accountData.pseudo)}&email=${encodeURIComponent(accountData.email)}`
      );
      const data = await response.json();
      
      if (!data.available) {
        // Show errors for each unavailable field
        data.errors.forEach(error => {
          toast.error(error.message);
        });
        setCheckingAvailability(false);
        return;
      }
      
      // All good, proceed to step 2
      setStep(2);
    } catch (error) {
      toast.error('Erreur lors de la vérification. Veuillez réessayer.');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!profileData.description || profileData.description.length < 50) {
      toast.error('La description doit contenir au moins 50 caractères');
      return;
    }

    if (!profileData.profile_type || !profileData.location) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Validation for couple
    if (profileData.profile_type === 'couple') {
      const { couple_man, couple_woman } = profileData;
      if (!couple_man.age || !couple_man.body_type || !couple_man.ethnicity || !couple_man.eye_color || !couple_man.sexual_orientation || !couple_man.smoker || !couple_man.experience_level ||
          !couple_woman.age || !couple_woman.body_type || !couple_woman.ethnicity || !couple_woman.eye_color || !couple_woman.sexual_orientation || !couple_woman.smoker || !couple_woman.experience_level) {
        toast.error('Veuillez remplir tous les champs pour l\'homme et la femme');
        return;
      }
    } else {
      // Validation for single person
      if (!profileData.age || !profileData.body_type || !profileData.ethnicity || !profileData.eye_color || !profileData.sexual_orientation || !profileData.smoker || !profileData.experience_level) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }
    }
    
    setLoading(true);
    
    try {
      // 1. Register account first
      await register(accountData.email, accountData.password, accountData.pseudo);
      
      // 2. Prepare profile data
      const profilePayload = {
        description: profileData.description,
        profile_type: profileData.profile_type,
        location: profileData.location,
        city: profileData.city || null,
        department: profileData.department || null,
        region: profileData.region || null,
        coordinates: profileData.coordinates || null,
        sexual_preferences: profileData.sexual_preferences
      };

      if (profileData.profile_type === 'couple') {
        // Store couple data
        profilePayload.couple_man = {
          age: parseInt(profileData.couple_man.age),
          height: profileData.couple_man.height ? parseInt(profileData.couple_man.height) : null,
          body_type: profileData.couple_man.body_type,
          ethnicity: profileData.couple_man.ethnicity,
          eye_color: profileData.couple_man.eye_color,
          sexual_orientation: profileData.couple_man.sexual_orientation,
          smoker: profileData.couple_man.smoker,
          experience_level: profileData.couple_man.experience_level
        };
        profilePayload.couple_woman = {
          age: parseInt(profileData.couple_woman.age),
          height: profileData.couple_woman.height ? parseInt(profileData.couple_woman.height) : null,
          body_type: profileData.couple_woman.body_type,
          ethnicity: profileData.couple_woman.ethnicity,
          eye_color: profileData.couple_woman.eye_color,
          sexual_orientation: profileData.couple_woman.sexual_orientation,
          smoker: profileData.couple_woman.smoker,
          experience_level: profileData.couple_woman.experience_level
        };
        // Use average age for display
        profilePayload.age = Math.round((parseInt(profileData.couple_man.age) + parseInt(profileData.couple_woman.age)) / 2);
      } else {
        profilePayload.age = parseInt(profileData.age);
        profilePayload.height = profileData.height ? parseInt(profileData.height) : null;
        profilePayload.body_type = profileData.body_type;
        profilePayload.ethnicity = profileData.ethnicity;
        profilePayload.eye_color = profileData.eye_color;
        profilePayload.sexual_orientation = profileData.sexual_orientation;
        profilePayload.smoker = profileData.smoker;
        profilePayload.experience_level = profileData.experience_level;
      }
      
      // 3. Create profile
      await api.post('/profile', profilePayload);
      
      toast.success('Profil créé ! Ajoutez maintenant vos photos.');
      setStep(3);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la création du profil';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setPhotos([...photos, ...newPhotos]);
  };

  const removePhoto = (index) => {
    const newPhotos = [...photos];
    URL.revokeObjectURL(newPhotos[index].preview);
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
  };

  const handleFinalSubmit = async () => {
    if (photos.length < 2) {
      toast.error('Vous devez ajouter au minimum 2 photos');
      return;
    }

    setLoading(true);
    setUploading(true);

    try {
      // Upload photos (account and profile already created in step 2)
      for (let i = 0; i < photos.length; i++) {
        const formData = new FormData();
        formData.append('file', photos[i].file);
        formData.append('is_private', 'false');
        if (i === 0) {
          formData.append('is_profile_picture', 'true');
        }
        
        await api.post('/photos', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success('Inscription réussie ! Votre profil est en attente de validation.');
      navigate('/profile');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'upload des photos';
      toast.error(message);
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  const togglePreference = (pref) => {
    if (profileData.sexual_preferences.includes(pref)) {
      setProfileData({
        ...profileData,
        sexual_preferences: profileData.sexual_preferences.filter(p => p !== pref)
      });
    } else {
      setProfileData({
        ...profileData,
        sexual_preferences: [...profileData.sexual_preferences, pref]
      });
    }
  };

  return (
    <div className="min-h-screen bg-dark py-12 px-6">
      <div 
        className="fixed inset-0 bg-cover bg-center opacity-10 pointer-events-none"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/5594992/pexels-photo-5594992.jpeg)',
        }}
      />
      
      <div className="relative max-w-2xl mx-auto">
        {/* Progress indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-serif text-lg transition-colors duration-300 ${
                    s < step ? 'bg-gold text-dark' : 
                    s === step ? 'bg-gold/20 text-gold border border-gold' : 
                    'bg-dark-subtle text-gray-500 border border-white/10'
                  }`}
                >
                  {s < step ? <Check className="w-5 h-5" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-0.5 mx-2 ${s < step ? 'bg-gold' : 'bg-white/10'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-12 mt-3 text-sm text-gray-500">
            <span className={step === 1 ? 'text-gold' : ''}>Compte</span>
            <span className={step === 2 ? 'text-gold' : ''}>Profil</span>
            <span className={step === 3 ? 'text-gold' : ''}>Photos</span>
          </div>
        </div>

        <div className="glass-card p-8 md:p-12">
          {/* Header */}
          <div className="text-center mb-10">
            <Link to="/" className="inline-block mb-6">
              <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center">
                <span className="text-dark font-serif text-2xl">D</span>
              </div>
            </Link>
            <h1 className="font-serif text-3xl gold-gradient-text mb-2">
              {step === 1 && 'Créez votre compte'}
              {step === 2 && 'Complétez votre profil'}
              {step === 3 && 'Ajoutez vos photos'}
            </h1>
            <p className="text-gray-400 text-sm">
              {step === 1 && 'Première étape pour rejoindre notre communauté'}
              {step === 2 && 'Décrivez-vous pour être visible par la communauté'}
              {step === 3 && 'Minimum 2 photos requises'}
            </p>
          </div>

          {/* Step 1: Account */}
          {step === 1 && (
            <form onSubmit={handleAccountSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="pseudo" className="text-gray-400">Pseudo</Label>
                <div className="flex items-center border-b border-white/20 focus-within:border-gold transition-colors">
                  <User className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" />
                  <input
                    id="pseudo"
                    type="text"
                    placeholder="Votre pseudo unique (sans espace)"
                    value={accountData.pseudo}
                    onChange={(e) => {
                      // Remove spaces in real-time
                      const value = e.target.value.replace(/\s/g, '');
                      setAccountData(prev => ({ ...prev, pseudo: value }));
                    }}
                    pattern="^[a-zA-Z0-9À-ÿ_-]+$"
                    minLength={3}
                    maxLength={30}
                    className="flex-1 bg-transparent border-none outline-none text-white py-3 placeholder:text-white/30"
                    required
                    data-testid="register-pseudo"
                  />
                </div>
                <p className="text-xs text-gray-500">3 à 30 caractères, sans espace</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-400">Email</Label>
                <div className="flex items-center border-b border-white/20 focus-within:border-gold transition-colors">
                  <Mail className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" />
                  <input
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={accountData.email}
                    onChange={(e) => setAccountData(prev => ({ ...prev, email: e.target.value }))}
                    className="flex-1 bg-transparent border-none outline-none text-white py-3 placeholder:text-white/30"
                    required
                    data-testid="register-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-400">Mot de passe</Label>
                <div className="flex items-center border-b border-white/20 focus-within:border-gold transition-colors">
                  <Lock className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={accountData.password}
                    onChange={(e) => setAccountData(prev => ({ ...prev, password: e.target.value }))}
                    className="flex-1 bg-transparent border-none outline-none text-white py-3 placeholder:text-white/30"
                    required
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-500 hover:text-gold transition-colors ml-3 flex-shrink-0"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-gray-400">Confirmer le mot de passe</Label>
                <div className="flex items-center border-b border-white/20 focus-within:border-gold transition-colors">
                  <Lock className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" />
                  <input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={accountData.confirmPassword}
                    onChange={(e) => setAccountData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="flex-1 bg-transparent border-none outline-none text-white py-3 placeholder:text-white/30"
                    required
                    data-testid="register-confirm-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full btn-gold flex items-center justify-center gap-2"
                data-testid="register-step1-submit"
                disabled={checkingAvailability}
              >
                {checkingAvailability ? (
                  <>
                    <div className="w-4 h-4 border-2 border-dark/30 border-t-dark rounded-full animate-spin" />
                    Vérification...
                  </>
                ) : (
                  <>
                    Continuer
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </form>
          )}


          {/* Step 2: Profile */}
          {step === 2 && (
            <form onSubmit={handleProfileSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Type de profil</Label>
                  <NativeSelect value={profileData.profile_type} onChange={(value) => setProfileData(prev => ({ ...prev, profile_type: value }))} options={PROFILE_TYPES} data-testid="register-profile-type" required />
                </div>
                {profileData.profile_type !== 'couple' && (
                  <div className="space-y-2">
                    <Label className="text-gray-400">Âge</Label>
                    <Input type="number" min="18" max="99" placeholder="Âge" value={profileData.age} onChange={(e) => setProfileData(prev => ({ ...prev, age: e.target.value }))} className="input-gold" required={profileData.profile_type !== 'couple'} data-testid="register-age" />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-gray-400">Lieu d'habitation</Label>
                <CityAutocomplete
                  value={profileData.location}
                  onChange={(value) => setProfileData(prev => ({ ...prev, location: value }))}
                  onSelect={(locationData) => {
                    if (locationData) {
                      setProfileData(prev => ({ ...prev, location: locationData.location, city: locationData.city, department: locationData.department, region: locationData.region, coordinates: locationData.coordinates }));
                    } else {
                      setProfileData(prev => ({ ...prev, location: '', city: '', department: '', region: '', coordinates: null }));
                    }
                  }}
                  placeholder="Entrez votre ville"
                  required
                  data-testid="register-location"
                />
              </div>

              {profileData.profile_type !== 'couple' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-gray-400">Taille (cm)</Label><Input type="number" min="140" max="220" placeholder="170" value={profileData.height} onChange={(e) => setProfileData(prev => ({ ...prev, height: e.target.value }))} className="input-gold" data-testid="register-height" /></div>
                    <div className="space-y-2"><Label className="text-gray-400">Physique</Label><NativeSelect value={profileData.body_type} onChange={(value) => setProfileData(prev => ({ ...prev, body_type: value }))} options={BODY_TYPES} data-testid="register-body-type" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-gray-400">Origine</Label><NativeSelect value={profileData.ethnicity} onChange={(value) => setProfileData(prev => ({ ...prev, ethnicity: value }))} options={ETHNICITIES} data-testid="register-ethnicity" /></div>
                    <div className="space-y-2"><Label className="text-gray-400">Couleur des yeux</Label><NativeSelect value={profileData.eye_color} onChange={(value) => setProfileData(prev => ({ ...prev, eye_color: value }))} options={EYE_COLORS} data-testid="register-eye-color" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-gray-400">Orientation</Label><NativeSelect value={profileData.sexual_orientation} onChange={(value) => setProfileData(prev => ({ ...prev, sexual_orientation: value }))} options={getOrientationsForGender(profileData.profile_type)} data-testid="register-orientation" /></div>
                    <div className="space-y-2"><Label className="text-gray-400">Fumeur</Label><NativeSelect value={profileData.smoker} onChange={(value) => setProfileData(prev => ({ ...prev, smoker: value }))} options={SMOKER_STATUS} data-testid="register-smoker" /></div>
                  </div>
                  <div className="space-y-2"><Label className="text-gray-400">Niveau d'expérience</Label><NativeSelect value={profileData.experience_level} onChange={(value) => setProfileData(prev => ({ ...prev, experience_level: value }))} options={EXPERIENCE_LEVELS} data-testid="register-experience" /></div>
                </>
              )}

              {profileData.profile_type === 'couple' && (
                <div className="space-y-6">
                  <div className="p-4 bg-pink-500/10 border border-pink-500/30 rounded-lg">
                    <h3 className="text-pink-400 font-semibold mb-4">Elle</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label className="text-gray-400">Âge</Label><Input type="number" min="18" max="99" placeholder="Âge" value={profileData.couple_woman.age} onChange={(e) => setProfileData(prev => ({ ...prev, couple_woman: { ...prev.couple_woman, age: e.target.value } }))} className="input-gold" required /></div>
                      <div className="space-y-2"><Label className="text-gray-400">Taille</Label><Input type="number" min="140" max="220" placeholder="165" value={profileData.couple_woman.height} onChange={(e) => setProfileData(prev => ({ ...prev, couple_woman: { ...prev.couple_woman, height: e.target.value } }))} className="input-gold" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2"><Label className="text-gray-400">Physique</Label><NativeSelect value={profileData.couple_woman.body_type} onChange={(value) => setProfileData(prev => ({ ...prev, couple_woman: { ...prev.couple_woman, body_type: value } }))} options={BODY_TYPES} /></div>
                      <div className="space-y-2"><Label className="text-gray-400">Origine</Label><NativeSelect value={profileData.couple_woman.ethnicity} onChange={(value) => setProfileData(prev => ({ ...prev, couple_woman: { ...prev.couple_woman, ethnicity: value } }))} options={ETHNICITIES} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2"><Label className="text-gray-400">Yeux</Label><NativeSelect value={profileData.couple_woman.eye_color} onChange={(value) => setProfileData(prev => ({ ...prev, couple_woman: { ...prev.couple_woman, eye_color: value } }))} options={EYE_COLORS} /></div>
                      <div className="space-y-2"><Label className="text-gray-400">Orientation</Label><NativeSelect value={profileData.couple_woman.sexual_orientation} onChange={(value) => setProfileData(prev => ({ ...prev, couple_woman: { ...prev.couple_woman, sexual_orientation: value } }))} options={getOrientationsForGender('femme')} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2"><Label className="text-gray-400">Fumeur</Label><NativeSelect value={profileData.couple_woman.smoker} onChange={(value) => setProfileData(prev => ({ ...prev, couple_woman: { ...prev.couple_woman, smoker: value } }))} options={SMOKER_STATUS} /></div>
                      <div className="space-y-2"><Label className="text-gray-400">Expérience</Label><NativeSelect value={profileData.couple_woman.experience_level} onChange={(value) => setProfileData(prev => ({ ...prev, couple_woman: { ...prev.couple_woman, experience_level: value } }))} options={EXPERIENCE_LEVELS} /></div>
                    </div>
                  </div>
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <h3 className="text-blue-400 font-semibold mb-4">Lui</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label className="text-gray-400">Âge</Label><Input type="number" min="18" max="99" placeholder="Âge" value={profileData.couple_man.age} onChange={(e) => setProfileData(prev => ({ ...prev, couple_man: { ...prev.couple_man, age: e.target.value } }))} className="input-gold" required /></div>
                      <div className="space-y-2"><Label className="text-gray-400">Taille</Label><Input type="number" min="140" max="220" placeholder="175" value={profileData.couple_man.height} onChange={(e) => setProfileData(prev => ({ ...prev, couple_man: { ...prev.couple_man, height: e.target.value } }))} className="input-gold" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2"><Label className="text-gray-400">Physique</Label><NativeSelect value={profileData.couple_man.body_type} onChange={(value) => setProfileData(prev => ({ ...prev, couple_man: { ...prev.couple_man, body_type: value } }))} options={BODY_TYPES} /></div>
                      <div className="space-y-2"><Label className="text-gray-400">Origine</Label><NativeSelect value={profileData.couple_man.ethnicity} onChange={(value) => setProfileData(prev => ({ ...prev, couple_man: { ...prev.couple_man, ethnicity: value } }))} options={ETHNICITIES} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2"><Label className="text-gray-400">Yeux</Label><NativeSelect value={profileData.couple_man.eye_color} onChange={(value) => setProfileData(prev => ({ ...prev, couple_man: { ...prev.couple_man, eye_color: value } }))} options={EYE_COLORS} /></div>
                      <div className="space-y-2"><Label className="text-gray-400">Orientation</Label><NativeSelect value={profileData.couple_man.sexual_orientation} onChange={(value) => setProfileData(prev => ({ ...prev, couple_man: { ...prev.couple_man, sexual_orientation: value } }))} options={getOrientationsForGender('homme')} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2"><Label className="text-gray-400">Fumeur</Label><NativeSelect value={profileData.couple_man.smoker} onChange={(value) => setProfileData(prev => ({ ...prev, couple_man: { ...prev.couple_man, smoker: value } }))} options={SMOKER_STATUS} /></div>
                      <div className="space-y-2"><Label className="text-gray-400">Expérience</Label><NativeSelect value={profileData.couple_man.experience_level} onChange={(value) => setProfileData(prev => ({ ...prev, couple_man: { ...prev.couple_man, experience_level: value } }))} options={EXPERIENCE_LEVELS} /></div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <Label className="text-gray-400">Envies</Label>
                <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2" data-testid="register-preferences">
                  {SEXUAL_PREFERENCES.map(pref => (
                    <button
                      key={pref}
                      type="button"
                      onClick={() => togglePreference(pref)}
                      className={`px-3 py-1.5 text-xs rounded-none border transition-colors ${
                        profileData.sexual_preferences.includes(pref)
                          ? 'border-gold bg-gold/20 text-gold'
                          : 'border-white/20 text-gray-400 hover:border-gold/50'
                      }`}
                    >
                      {pref}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-400">Description</Label>
                <Textarea
                  placeholder="Décrivez-vous, vos attentes... (minimum 50 caractères)"
                  value={profileData.description}
                  onChange={(e) => setProfileData(prev => ({ ...prev, description: e.target.value }))}
                  className="bg-transparent border-white/20 text-white min-h-[120px] focus:border-gold"
                  required
                  data-testid="register-description"
                />
                <p className="text-xs text-gray-500">{profileData.description.length}/50 caractères minimum</p>
              </div>

              <div className="flex gap-4">
                <Button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 btn-outline-gold flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour
                </Button>
                <Button
                  type="submit"
                  className="flex-1 btn-gold flex items-center justify-center gap-2"
                  data-testid="register-step2-submit"
                >
                  Continuer
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </form>
          )}

          {/* Step 3: Photos */}
          {step === 3 && (
            <div className="space-y-6">
              <div 
                className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center hover:border-gold/50 transition-colors cursor-pointer"
                onClick={() => document.getElementById('photo-upload').click()}
                data-testid="photo-upload-zone"
              >
                <Upload className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400 mb-2">Cliquez ou glissez vos photos ici</p>
                <p className="text-xs text-gray-500">PNG, JPG jusqu'à 10MB</p>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </div>

              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-4" data-testid="photo-preview-grid">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative group aspect-square">
                      <img
                        src={photo.preview}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover rounded-sm"
                      />
                      {index === 0 && (
                        <span className="absolute top-2 left-2 px-2 py-1 bg-gold text-dark text-xs font-semibold">
                          PROFIL
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-dark-subtle border border-white/10 rounded-sm p-4">
                <h4 className="text-gold text-sm font-semibold mb-2">Conseils pour vos photos</h4>
                <p className="text-sm text-gray-400 italic">
                  Nous vous conseillons de proposer des photos de vous élégantes... et suggestives si vous voulez rester discret...
                </p>
              </div>

              <div className="flex gap-4">
                <Button
                  type="button"
                  onClick={() => setStep(2)}
                  className="flex-1 btn-outline-gold flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour
                </Button>
                <Button
                  type="button"
                  onClick={handleFinalSubmit}
                  disabled={loading || photos.length < 2}
                  className="flex-1 btn-gold flex items-center justify-center gap-2"
                  data-testid="register-final-submit"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-dark/30 border-t-dark rounded-full animate-spin" />
                      {uploading ? 'Upload...' : 'Création...'}
                    </>
                  ) : (
                    <>
                      Terminer l'inscription
                      <Check className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>

              {photos.length < 2 && (
                <p className="text-center text-sm text-red-400">
                  Vous devez ajouter au minimum 2 photos ({photos.length}/2)
                </p>
              )}
            </div>
          )}

          <div className="mt-8 text-center">
            <p className="text-gray-400 text-sm">
              Déjà membre ?{' '}
              <Link to="/login" className="text-gold hover:text-gold-light transition-colors" data-testid="login-link">
                Se connecter
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
